// Playback for a /speak topic page.
//
// ⛔ No React here. The dialogue is already rendered by the server — this is
// progressive enhancement over it, and the page has to stay complete and
// readable for a reader whose device cannot speak. The choosing lives in
// utils/speakPlayback so it is testable without a browser; this file is the
// wiring: find the elements, listen, speak, mark.

import {
  shouldRevealControls,
  assignRoleVoices,
  choosableVoices,
  voiceOptions,
  voiceId,
  rateFor,
  playbackOrder
} from 'utils/speakPlayback'

// The reader's pick, per side. Stored in this browser only: voice ids differ
// from device to device, so a server-side setting would be wrong on every
// other device the reader owns.
const VOICE_STORAGE_KEY = 'speak:voices'

// ⛔ Every access guarded: a private window or blocked site data throws, and
// playback must work without a saved choice.
function loadSavedVoices() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(VOICE_STORAGE_KEY) || '{}')
    return saved && typeof saved === 'object' ? saved : {}
  } catch (e) {
    return {}
  }
}

function saveVoices(saved) {
  try {
    window.localStorage.setItem(VOICE_STORAGE_KEY, JSON.stringify(saved))
  } catch (e) { /* the choice lasts for this visit only */ }
}

// Turns describeVoice's keys into the reader's words, e.g. 美式口音・女生・比較自然.
function voiceLabelFormatter(labels) {
  return ({ accent, gender, quality, online }) => {
    const parts = [labels.accent?.[accent] || labels.accent?.other]
    if (gender) parts.push(labels.gender?.[gender])
    if (quality) parts.push(labels.quality?.[quality])
    if (online) parts.push(labels.online)

    return parts.filter(Boolean).join(labels.separator || ' · ')
  }
}

function setup() {
  const root = document.getElementById('speak-topic')
  if (!root) return

  // ⛔ The only gate that matters. A browser with no speech synthesis leaves
  // every control hidden and the page is still the whole dialogue.
  const synth = window.speechSynthesis
  if (!synth) return

  const lineNodes = Array.from(root.querySelectorAll('li[data-speak-line]'))
  if (lineNodes.length === 0) return

  const dialogueControls = root.querySelector('[data-speak-dialogue]')
  const lineControls = Array.from(root.querySelectorAll('[data-speak-controls]'))

  const picker = root.querySelector('[data-speak-voice-picker]')
  const voiceSelects = picker ? Array.from(picker.querySelectorAll('select[data-speak-voice-select]')) : []
  let formatVoice = null
  try {
    formatVoice = voiceLabelFormatter(JSON.parse(picker?.dataset.speakVoiceLabels || '{}'))
  } catch (e) {
    formatVoice = null
  }

  let roleVoices = { other: null, learner: null }
  let savedVoices = loadSavedVoices()
  let revealed = false

  // Every utterance carries the generation it was started in. `cancel()` fires
  // `onend` on the utterance it kills in some browsers, which would otherwise
  // advance the whole-dialogue sequence one line for every interruption.
  let generation = 0

  function clearMarks() {
    lineNodes.forEach((node) => node.removeAttribute('data-speaking'))
  }

  function reveal() {
    if (revealed) return
    revealed = true

    lineControls.forEach((node) => node.classList.remove('hidden'))
    if (dialogueControls) dialogueControls.classList.remove('hidden')
  }

  // iOS Safari returns an empty list on the first call and fills it in later,
  // so this runs again on every `voiceschanged`. A device that can in fact
  // speak must not be left with the controls hidden because of one early read.
  function refreshVoices() {
    const voices = synth.getVoices()

    roleVoices = assignRoleVoices(voices, savedVoices)
    if (shouldRevealControls(voices)) reveal()
    fillVoiceSelects(voices)
  }

  // Rebuilt when voiceschanged brings a different list, so a list that arrives
  // late or grows (Chrome adds its online voices after the local ones) is
  // offered in full.
  let optionsKey = ''

  function fillVoiceSelects(voices) {
    if (!formatVoice || voiceSelects.length === 0) return

    const choosable = choosableVoices(voices, [roleVoices.other, roleVoices.learner])
    // A picker with one option is a control that does nothing.
    if (picker) picker.hidden = choosable.length < 2

    // ⛔ Rebuilding the options closes a dropdown the reader has open, so only
    // rebuild when the list is actually different.
    const key = choosable.map(voiceId).join('\n')
    if (key !== optionsKey) {
      optionsKey = key
      const options = voiceOptions(choosable, formatVoice)

      voiceSelects.forEach((select) => {
        select.replaceChildren(
          ...options.map(({ id, label }) => {
            const option = document.createElement('option')
            option.value = id
            option.textContent = label
            return option
          })
        )
      })
    }

    syncVoiceSelects()
  }

  // Both selects, not just the one that changed: picking the other side's
  // voice for this side moves the other side to a new default.
  function syncVoiceSelects() {
    voiceSelects.forEach((select) => {
      select.value = voiceId(roleVoices[select.dataset.speakVoiceSelect])
    })
  }

  // A sample in the new voice, taken from that side's own first line, so the
  // reader hears the pick on a sentence they are about to practise.
  function previewRole(role) {
    const node = lineNodes.find((line) => line.dataset.speakSpeaker === role) || lineNodes[0]
    speakLine(node, 'normal')
  }

  voiceSelects.forEach((select) => {
    select.addEventListener('change', () => {
      const role = select.dataset.speakVoiceSelect
      savedVoices = { ...savedVoices, [role]: select.value }
      saveVoices(savedVoices)

      roleVoices = assignRoleVoices(synth.getVoices(), savedVoices)
      syncVoiceSelects()
      previewRole(role)
    })
  })

  function utteranceFor(node, speed) {
    const text = node.dataset.speakText
    if (!text) return null

    const utterance = new SpeechSynthesisUtterance(text)
    const voice = roleVoices[node.dataset.speakSpeaker] || roleVoices.other

    if (voice) {
      utterance.voice = voice
      utterance.lang = voice.lang
    } else {
      utterance.lang = 'en-US'
    }
    utterance.rate = rateFor(speed)

    return utterance
  }

  // One line, on demand. Whatever was being said stops first: two voices over
  // each other teaches nothing, and the mark would be left on two lines.
  function speakLine(node, speed) {
    const mine = ++generation

    synth.cancel()
    clearMarks()

    const utterance = utteranceFor(node, speed)
    if (!utterance) return

    const finish = () => {
      if (mine !== generation) return
      node.removeAttribute('data-speaking')
    }

    utterance.onstart = () => {
      if (mine !== generation) return
      node.setAttribute('data-speaking', 'true')
    }
    // ⛔ Both, not just onend: a voice that fails mid-sentence would otherwise
    // leave the line marked as speaking for the rest of the visit.
    utterance.onend = finish
    utterance.onerror = finish

    synth.speak(utterance)
  }

  // The whole scene, in number order, alternating the two voices. This is the
  // practice neither the Threads post nor a printed page can offer.
  function playAll() {
    const mine = ++generation

    synth.cancel()
    clearMarks()

    const queue = playbackOrder(
      lineNodes.map((node) => ({ number: Number(node.dataset.speakLine), node }))
    )

    const speakAt = (index) => {
      if (mine !== generation || index >= queue.length) {
        clearMarks()
        return
      }

      const { node } = queue[index]
      const utterance = utteranceFor(node, 'normal')
      if (!utterance) {
        speakAt(index + 1)
        return
      }

      utterance.onstart = () => {
        if (mine !== generation) return

        clearMarks()
        node.setAttribute('data-speaking', 'true')
        // The dialogue is longer than a screen, so the line being spoken has to
        // come to the reader rather than the reader hunting for it.
        node.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      utterance.onend = () => speakAt(index + 1)
      // A line the device chokes on stops the run rather than silently
      // skipping ahead — a run that plays 9 of 12 lines looks like it worked.
      utterance.onerror = () => {
        if (mine !== generation) return
        clearMarks()
      }

      synth.speak(utterance)
    }

    speakAt(0)
  }

  function stop() {
    generation += 1
    synth.cancel()
    clearMarks()
  }

  lineNodes.forEach((node) => {
    node.querySelectorAll('button[data-speak-play]').forEach((button) => {
      button.addEventListener('click', () => speakLine(node, button.dataset.speakPlay))
    })
  })

  if (dialogueControls) {
    const playAllButton = dialogueControls.querySelector('button[data-speak-play-all]')
    const stopButton = dialogueControls.querySelector('button[data-speak-stop]')

    if (playAllButton) playAllButton.addEventListener('click', playAll)
    if (stopButton) stopButton.addEventListener('click', stop)
  }

  // Leaving the page mid-sentence otherwise keeps the voice going in Chrome.
  window.addEventListener('pagehide', () => synth.cancel())

  refreshVoices()
  synth.addEventListener?.('voiceschanged', refreshVoices)
}

document.addEventListener('DOMContentLoaded', setup)
